import { v4 as uuidv4 } from "uuid";
import { ColumnJson, DataTableDataInterface } from "app/utils/types";

export const editableColumns: string[] = [
  "employeePremium",
  "employerPremium",
  "employerAdminFee",
  "planOriginationFees",
  "planLoanRate",
  "minimumAFR",
  "employeePrincipalPayment",
];

export const planAdminSplitDollarTableDataJson: DataTableDataInterface = {
  pagination: 10,
  rowsPerPage: [25, 50, 100],
  styles: {
    minWidth: "95rem",
    minHeight: "",
  },
  scrollable: true,
  scrollHeight: "500px",
  onRowClickNavigateTo: "/agent/contact",
  column: [],
};
export const planAdminSplitDollarColumnSchema = (
  label: string,
  field: string,
  isFrozen = false,
  classNames?: string,
): ColumnJson => {
  let newLabel = label;
  if (label === "Employee Out Of Pocket") {
    newLabel = "Employee Out of Pocket";
  }
  return {
    id: `${field}-${uuidv4()}`,
    body: {},
    field,
    label: newLabel,
    frozen: isFrozen,
    header: false,
    filters: {
      filterType: "text",
      value: "",
    },
    editColumn: editableColumns.findIndex((col) => col === field) > -1,
    align: "right",
    alignHeader: "center",
    editorType: "decimal",
    classNames,
    ...((field === "planLoanRate" || field === "minimumAFR") && {
      maxFractionDigits: 3,
    }),
  };
};

export const headerColumnGroup = {
  EOP: ["employeePremium", "employeeInterestPayment", "employeeOOP"],
  EOPP: ["employerPremium", "employerAdminFee", "planOriginationFees", "employerOOP"],
  TPPIC: ["totalPolicyPremium"],
  "INR%": ["planLoanRate", "minimumAFR"],
  INR$: ["planInterest", "minimumAFRInterest"],
  IC: ["imputedCompensationToEmployee"],
  OB: ["boyOutstandingLoanBalance", "eoyOutstandingLoanBalance"],
  EPP: ["employeePrincipalPayment"],
};

export const commonColumns = ["policyYear", "insuredAge", "planYear"];

export const prepareRows = (
  rowData: any,
  key: string,
  value: number,
  rowIdx: number,
  planSDSchema: any,
  policyInputCSVData: any,
  accrueInterest: boolean,
  loanExit?: string,
) => {
  const data = rowData[rowIdx];

  const adminSplitDollarRow = { ...data };

  adminSplitDollarRow[key] = value;

  const prevRowData = rowData.find(
    (row: any) =>
      +row.insuredAge === +adminSplitDollarRow.insuredAge - 1 &&
      +row.policyYear === +adminSplitDollarRow.policyYear - 1 &&
      +row.planYear === +adminSplitDollarRow.planYear - 1,
  );

  // `loanExit` is a top-level value like "YEAR_11", not a per-row flag. Rows
  // are always in sequential plan-year order starting at 1 (row index 0 =
  // plan year 1), so "YEAR_11" maps directly to array index 10.
  const loanExitMatch = /(\d+)/.exec(loanExit || "");
  const loanExitRowIndex = loanExitMatch ? Number(loanExitMatch[1]) - 1 : -1;

  // At the loan exit row, Employee Principal Payment is not user-entered — it
  // always equals the prior year's EOY Outstanding Loan Balance, so it stays
  // in sync as earlier rows are edited.
  if (loanExitRowIndex !== -1 && rowIdx === loanExitRowIndex && prevRowData) {
    adminSplitDollarRow.employeePrincipalPayment = +prevRowData.eoyOutstandingLoanBalance;
  }

  // BOY Outstanding Loan Balance (Q)
  //   First Year  (Q4 = G4):        BOY = Employer Premium
  //   Subsequent  (Qn = Gn + Rn-1 - Sn):
  //     BOY = Prior Year's EOY + Current Employer Premium
  //           - Current Year's Employee Principal Payment
  // The first row is a special case and does NOT subtract the Employee
  // Principal Payment. Subsequent rows subtract the CURRENT row's value.
  adminSplitDollarRow.boyOutstandingLoanBalance = prevRowData
    ? +prevRowData.eoyOutstandingLoanBalance +
      +adminSplitDollarRow.employerPremium -
      +adminSplitDollarRow.employeePrincipalPayment
    : +adminSplitDollarRow.employerPremium;

  adminSplitDollarRow.planInterest =
    (+adminSplitDollarRow.planLoanRate / 100) * +adminSplitDollarRow.boyOutstandingLoanBalance;

  if (accrueInterest) {
    adminSplitDollarRow.employeeInterestPayment = 0;
  } else {
    adminSplitDollarRow.employeeInterestPayment = +adminSplitDollarRow.planInterest;
  }

  adminSplitDollarRow.employeeOOP =
    +adminSplitDollarRow.employeePremium + +adminSplitDollarRow.employeeInterestPayment;

  adminSplitDollarRow.employerOOP =
    +adminSplitDollarRow.employerPremium +
    +adminSplitDollarRow.employerAdminFee +
    +adminSplitDollarRow.planOriginationFees;

  adminSplitDollarRow.totalPolicyPremium =
    +adminSplitDollarRow.employerPremium + +adminSplitDollarRow.employeePremium;

  adminSplitDollarRow.minimumAFRInterest =
    (+adminSplitDollarRow.minimumAFR / 100) * +adminSplitDollarRow.boyOutstandingLoanBalance;

  if (accrueInterest) {
    adminSplitDollarRow.imputedCompensationToEmployee = 0;
  } else {
    adminSplitDollarRow.imputedCompensationToEmployee = Math.max(
      +adminSplitDollarRow.minimumAFRInterest - +adminSplitDollarRow.employeeInterestPayment,
      0,
    );
  }

  // EOY Outstanding Loan Balance =
  //   BOY + MAX(Plan Interest, Minimum AFR Interest) - Employee Interest Payment
  // Applied regardless of the accrueInterest flag: employeeInterestPayment is
  // already forced to 0 when accrueInterest === true, so the same formula holds.
  adminSplitDollarRow.eoyOutstandingLoanBalance = Math.max(
    +adminSplitDollarRow.boyOutstandingLoanBalance +
      Math.max(+adminSplitDollarRow.planInterest, +adminSplitDollarRow.minimumAFRInterest) -
      +adminSplitDollarRow.employeeInterestPayment,
    0,
  );

  rowData[rowIdx] = adminSplitDollarRow;

  for (let i = rowIdx + 1; i < rowData.length; i++) {
    // At the loan exit row, Employee Principal Payment always equals the
    // prior year's (now-recalculated) EOY Outstanding Loan Balance.
    if (i === loanExitRowIndex) {
      rowData[i].employeePrincipalPayment = +rowData[i - 1].eoyOutstandingLoanBalance;
    }

    // BOY Outstanding Loan Balance = Previous Year's EOY + Employer Premium -
    // Current Year's Employee Principal Payment
    // NOTE: subtraction now uses the CURRENT row's employeePrincipalPayment
    // (per Excel workbook logic), not the previous row's.
    rowData[i].boyOutstandingLoanBalance =
      +rowData[i - 1].eoyOutstandingLoanBalance +
      +rowData[i].employerPremium -
      +rowData[i].employeePrincipalPayment;

    rowData[i].planInterest =
      (+rowData[i].planLoanRate / 100) * +rowData[i].boyOutstandingLoanBalance;

    rowData[i].minimumAFRInterest =
      (+rowData[i].minimumAFR / 100) * +rowData[i].boyOutstandingLoanBalance;

    // Set employee interest payment based on accrue interest setting
    if (accrueInterest) {
      rowData[i].employeeInterestPayment = 0;
    } else {
      rowData[i].employeeInterestPayment = +rowData[i].planInterest;
    }

    // Update other calculated fields
    rowData[i].employeeOOP = +rowData[i].employeePremium + +rowData[i].employeeInterestPayment;

    rowData[i].employerOOP =
      +rowData[i].employerPremium + +rowData[i].employerAdminFee + +rowData[i].planOriginationFees;

    rowData[i].totalPolicyPremium = +rowData[i].employerPremium + +rowData[i].employeePremium;

    if (accrueInterest) {
      rowData[i].imputedCompensationToEmployee = 0;
    } else {
      rowData[i].imputedCompensationToEmployee = Math.max(
        +rowData[i].minimumAFRInterest - +rowData[i].employeeInterestPayment,
        0,
      );
    }

    // EOY Outstanding Loan Balance =
    //   BOY + MAX(Plan Interest, Minimum AFR Interest) - Employee Interest Payment
    // Applied regardless of the accrueInterest flag: employeeInterestPayment is
    // already forced to 0 when accrueInterest === true, so the same formula holds.
    rowData[i].eoyOutstandingLoanBalance = Math.max(
      +rowData[i].boyOutstandingLoanBalance +
        Math.max(+rowData[i].planInterest, +rowData[i].minimumAFRInterest) -
        +rowData[i].employeeInterestPayment,
      0,
    );
  }

  const keys1 = Object.keys(planSDSchema);
  const keys2 = Object.keys(adminSplitDollarRow);

  const commonKeys = keys1.filter((k) => keys2.includes(k));

  let splitDollarRow = commonKeys.reduce(
    (acc, k) => ({
      ...acc,
      [k]: adminSplitDollarRow[k],
    }),
    {},
  );

  splitDollarRow = {
    ...splitDollarRow,
    endOfYearOutstandingLoanBalance: adminSplitDollarRow.eoyOutstandingLoanBalance,
  };

  const splitDollarRowArr = [splitDollarRow];

  for (let i = rowIdx + 1; i < rowData.length; i++) {
    const newRow = commonKeys.reduce(
      (acc, k) => ({
        ...acc,
        [k]: rowData[i][k],
      }),
      {},
    );
    splitDollarRowArr.push({
      ...newRow,
      endOfYearOutstandingLoanBalance: rowData[i].eoyOutstandingLoanBalance,
    });
  }

  return {
    adminSplitDollarRow,
    splitDollarRowArr,
    updatedRows: rowData,
  };
};
